import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { UnitSystem } from "@my-dld/utils/UnitUtils";
import React, { Component } from "react";
import { getAppServices } from "@10x/coreapp";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import Strings from "@my-dld/assets/strings";
import { TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import PropertyTable from "@my-dld/components/Portfolio/PropertyTable";
import PropertyCard from "@my-dld/components/Portfolio/PropertyCard";
import PortfolioContextWrapper, { IPortfolioContext, Portfolio, PortfolioContext } from "@my-dld/contexts/PortfolioContext";
import { Redirect } from "react-router";

interface Props {
	property?: AssetCard;
	properties: AssetCard[];
	onContinue: ( properties: AssetCard[] ) => void;
}

interface State {
	property?: AssetCard;
	properties: AssetCard[];
	sProperties: AssetCard[];
	unit: UnitSystem;
}

class PropertyPicker extends Component<Props, State> {
	static contextType = PortfolioContext;
	context!: React.ContextType<typeof PortfolioContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			properties: [],
			sProperties: [],
			unit: getAppServices().storeService.getStore().getState().User.unit,
		};
	}

	componentDidMount() {
		const { property, properties } = this.props;
		const bucket = getBucket( this.context, property );
		const oProperties = getMasterTypeProperties( bucket, property );
		oProperties.sort( ( a: AssetCard, b: AssetCard ) => {
			return ( properties.indexOf( a ) > -1 ) ? -1 : ( properties.indexOf( b ) > -1 ) ? 1 : 0;
		} );
		this.setState( {
			property: property,
			properties: oProperties,
			sProperties: properties,
		} );
	}

	render() {
		if ( this.props.property == null ) {
			return <Redirect to={ "/mydld" }/>;
		}
		const { properties, sProperties } = this.state;
		return <ListGridDataView
			isLoading={ false }
			dataAvailable={ properties.length > 0 }
			title={ Strings.add_more_properties }
			onUnitSystemChange={ this.onUnitChange }
			renderListView={ this.renderListView }
			renderGridView={ this.renderGridView }>
			<div className="btn-alignment">
				<div>
					{ sProperties.length === 0 && <span className="alert alert-warning">
                        { Strings.please_select_from_below_properties }
                    </span> }
					{ sProperties.length > 0 && <div>
                    <span className="properties-selected">
                        { `${ Strings.selected_properties } (${ sProperties.length })` }
                    </span>
						<button
							type="button" className="btn btn-sm btn-grey"
							onClick={ this.onClear }>{ Strings.clear_all }</button>
					</div> }
				</div>
				<button
					className="btn btn_1" type="button"
					onClick={ this.onContinue }>{ Strings.proceed }</button>
			</div>
		</ListGridDataView>;
	}

	private onUnitChange = ( unit: UnitSystem ) => {
		this.setState( { unit: unit } );
	};

	private renderListView = () => {
		const { property, properties, unit, sProperties } = this.state;
		return <>
			{ property?.propertyTypeId === TabuPropertyType.LAND &&
				<PropertyTable
					type={ TabuPropertyType.LAND } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ sProperties }
					onSelect={ this.onSelect }/> }
			{ property?.propertyTypeId === TabuPropertyType.VILLA &&
				<PropertyTable
					type={ TabuPropertyType.VILLA } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ sProperties }
					onSelect={ this.onSelect }/> }
			{ property?.propertyTypeId === TabuPropertyType.UNIT &&
				<PropertyTable
					type={ TabuPropertyType.UNIT } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ sProperties }
					onSelect={ this.onSelect }/> }
		</>;
	};

	private renderGridView = () => {
		const { property, properties, unit, sProperties } = this.state;
		return <React.Fragment>
			{ property?.propertyTypeId === TabuPropertyType.LAND && <div className="land">
				<PropertyCard
					type={ TabuPropertyType.LAND } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ sProperties }
					onSelect={ this.onSelect }/>
			</div> }
			{ property?.propertyTypeId === TabuPropertyType.VILLA && <div className="villa">
				<PropertyCard
					type={ TabuPropertyType.VILLA } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ sProperties }
					onSelect={ this.onSelect }/>
			</div> }
			{ property?.propertyTypeId === TabuPropertyType.UNIT && <div className="villa">
				<PropertyCard
					type={ TabuPropertyType.UNIT } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ sProperties }
					onSelect={ this.onSelect }/>
			</div> }
		</React.Fragment>;
	};

	private onSelect = ( property: AssetCard, checked: boolean ) => {
		const { sProperties } = this.state;
		if ( checked ) {
			this.setState( {
				sProperties: [ ...sProperties, property ],
			} );
		} else {
			this.setState( {
				sProperties: sProperties.filter( p => p.propertyId !== property.propertyId ),
			} );
		}
	};

	private onClear = () => {
		this.setState( {
			sProperties: [],
		} );
	};

	private onContinue = () => {
		this.props.onContinue( this.state.sProperties );
	};
}

function getBucket( ctx: IPortfolioContext, masterProperty?: AssetCard ): Portfolio<AssetCard> {
	if ( masterProperty?.isAgent ) {
		const p = ctx.proxyAssets.find( x => x.id + "" === masterProperty?.agentCompany?.number + "" );
		if ( p ) {
			return p;
		}
	}
	return ctx.assets;
}

function getMasterTypeProperties( assets: Portfolio<AssetCard>, masterProperty?: AssetCard ) {
	let properties: AssetCard[] = [];
	if ( masterProperty ) {
		if ( masterProperty.propertyTypeId === TabuPropertyType.LAND ) {
			properties = assets.lands || [];
		} else if ( ( masterProperty.propertyTypeId === TabuPropertyType.BUILDING ||
			masterProperty.propertyTypeId === TabuPropertyType.VILLA ) ) {
			if ( masterProperty.isVilla ) {
				properties = assets.villas || [];
			} else {
				properties = assets.buildings || [];
			}
		} else if ( masterProperty.propertyTypeId === TabuPropertyType.LAND ) {
			properties = assets.units || [];
		}
	}
	return properties.filter( p => p.propertyId !== masterProperty?.propertyId );
}

export default ( props: Props ) => <PortfolioContextWrapper>
	<PropertyPicker { ...props }/>
</PortfolioContextWrapper>;
